import { AlertTriangle, ArrowUpCircle, Package } from "lucide-react";
import { Link } from "react-router-dom";
import Button from "./Button";

interface QuotaWarningProps {
  type: "soft" | "hard";
  resourceType: "video" | "file" | "course" | "student" | "instructor" | "lesson";
  usagePercentage?: number;
  currentTier: string;
  nextTier?: string;
  upgradeMessage?: string;
  showAddonOption?: boolean;
  onClose?: () => void;
}

export default function QuotaWarning({
  type,
  resourceType,
  usagePercentage,
  currentTier,
  nextTier,
  upgradeMessage,
  showAddonOption = false,
  onClose,
}: QuotaWarningProps) {
  const resourceNames: Record<string, string> = {
    video: "พื้นที่วิดีโอ",
    file: "พื้นที่ไฟล์เอกสาร",
    course: "จำนวนคอร์ส",
    student: "จำนวนนักเรียน",
    instructor: "จำนวนผู้สอน",
    lesson: "จำนวนบทเรียน",
  };

  const getSoftWarningMessage = () => {
    return `คุณใช้${resourceNames[resourceType]}ไปแล้ว ${usagePercentage?.toFixed(0)}% ของแพ็กเกจปัจจุบัน หากมีการเพิ่ม${resourceType === "video" || resourceType === "file" ? "ไฟล์" : resourceNames[resourceType]}เพิ่มเติม แนะนำให้อัปเกรดแพ็กเกจเพื่อรองรับการเติบโต`;
  };

  const getHardBlockMessage = () => {
    if (currentTier === "yearly" && showAddonOption) {
      return `คุณใช้พื้นที่เกินแพ็ก Yearly คุณสามารถเพิ่มพื้นที่จัดเก็บเพิ่มเติม หรือเลือกติดต่อทีมงานเพื่อขยายระบบให้เหมาะกับการใช้งานของคุณ`;
    }

    return `แพ็กเกจปัจจุบันไม่รองรับการ${resourceType === "video" || resourceType === "file" ? "อัปโหลด" : "เพิ่ม"}${resourceNames[resourceType]}เพิ่มเติม กรุณาอัปเกรดแพ็กเกจเพื่อเพิ่มขีดความสามารถในการใช้งาน`;
  };

  if (type === "soft") {
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-4 mb-6 animate-fadeIn">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold text-yellow-900 mb-1">แจ้งเตือน: ใกล้ถึงขีดจำกัด</h3>
            <p className="text-sm text-yellow-800 mb-3">{getSoftWarningMessage()}</p>
            {upgradeMessage && (
              <p className="text-sm text-yellow-700 mb-3">{upgradeMessage}</p>
            )}
            <div className="flex gap-2">
              <Link to="/pricing#packages">
                <Button color="primary" full={false} className="text-sm px-3 py-2">
                  <ArrowUpCircle className="w-4 h-4 mr-1 inline" />
                  ดูแพ็กเกจ
                </Button>
              </Link>
              {onClose && (
                <Button color="secondary" full={false} className="text-sm px-3 py-2" onClick={onClose}>
                  ปิด
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-xl p-4 mb-6 animate-fadeIn">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-bold text-red-900 mb-1">ไม่สามารถดำเนินการได้</h3>
          <p className="text-sm text-red-800 mb-3">{getHardBlockMessage()}</p>
          {upgradeMessage && (
            <p className="text-sm text-red-700 mb-3">{upgradeMessage}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {nextTier && (
              <Link to={`/subscription/checkout?tier=${nextTier}`}>
                <Button color="primary" full={false} className="text-sm px-3 py-2">
                  <ArrowUpCircle className="w-4 h-4 mr-1 inline" />
                  อัปเกรดเป็น {nextTier.toUpperCase().replace("_", "+")}
                </Button>
              </Link>
            )}
            {showAddonOption && (
              <Link to="/subscription/addons">
                <Button color="secondary" full={false} className="text-sm px-3 py-2">
                  <Package className="w-4 h-4 mr-1 inline" />
                  เพิ่มพื้นที่จัดเก็บ
                </Button>
              </Link>
            )}
            {!nextTier && !showAddonOption && (
              <Link to="/pricing#packages">
                <Button color="primary" full={false} className="text-sm px-3 py-2">
                  <ArrowUpCircle className="w-4 h-4 mr-1 inline" />
                  ดูแพ็กเกจทั้งหมด
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
